import { FieldSetInputProps, FieldSetHookForm, FieldDesc, FieldDescProps, DatePickerSheet } from "components/voc";
import { useEffect, useState } from "react";
import styled from "styled-components";
import * as yup from "yup";
import { useModalState } from "hooks";
import { vocApi } from "apis";
import { useWebtoApp, useToast } from "hooks";

type InquiryFormProps = {
  onComplete: () => void;
};

const InquiryFormWrapper = styled.div`
  .form_field {
    margin-top: 0.3rem;
  }
  .form_field .btn_default {
    display: flex;
    width: 23rem;
    margin: 0 auto;
  }
`;

const privacyDesc: FieldDescProps = {
  title: "SK플래닛(주) 귀중",
  list: [
    { title: "수집, 이용 목적", desc: "광고 적립금 관련 고객 문의사항 처리" },
    { title: "수집, 이용 항목", desc: "답변 받을 이메일주소, 문의내용" },
    { title: "보유 및 이용기간", desc: "문의일로부터 3년 보관 후 파기", emph: true },
  ],
  noti: "고객님께서는 동의를 거부할 권리가 있으며, 동의 거부 시 문의사항 접수가 불가합니다.",
};

const InquiryForm = ({ onComplete }: InquiryFormProps) => {
  const sheetState = useModalState();
  const webToApp = useWebtoApp();
  const toast = useToast();

  const [dateLabel, setDateLabel] = useState("광고 참여 날짜, 시간대");

  // useEffect(() => {
  //   toast.show(
  //     <>
  //       <p>
  //         <strong>debug</strong>
  //       </p>
  //       {<p>{JSON.stringify(webToApp.state) || "파라미터 없음"}</p>}
  //     </>
  //   );
  // }, [webToApp, webToApp.state]);

  useEffect(() => {
    if (Object.keys(sheetState.emitState).length > 0) {
      setDateLabel("광고 참여 날짜");
    }
  }, [sheetState.emitState]);

  const handleSubmit = async (data: DynamicJsonTypes) => {
    const params: DynamicJsonTypes = {
      ...data,
      ad_alloc_date: sheetState.emitState?.alloc_date,
      ad_alloc_time: sheetState.emitState?.alloc_time.join(","),
    };

    try {
      const logParams = {
        ifa: webToApp.state?.ifa,
        "SKPAd-UUID": webToApp.state && webToApp.state["SKPAd-UUID"],
        ifv: webToApp.state?.ifa,
        device_id: webToApp.state?.device_id,
        // ifa: "ios_12",
        // "SKPAd-UUID": "ios_12",
        // ifv: "ios_12",
        // device_id: 5646,
        date: params.ad_alloc_date,
        time: params.ad_alloc_time,
      };

      const logs = await vocApi.getLog(logParams);

      const vocParams = {
        public_id: webToApp.state?.public_id,
        app_id: webToApp.state?.app_id,
        app_name: webToApp.state?.app_name,
        // public_id: "ios_12",
        // app_id: "123123123221",
        // app_name: "ios_12",
        ad_alloc_date: new Date(params.ad_alloc_date).toISOString(),
        ad_alloc_time: params.ad_alloc_time,
        email: params.email,
        cs_contents: params.cs_contents,
        ad_alloc_hist: JSON.stringify(logs.data),
        files: params.files,
      };

      await vocApi.inquiry(vocParams);

      onComplete();
    } catch (err) {
      console.log(err);
      // toast.show(JSON.parse(JSON.stringify(err)).message);
      toast.show("일시적인 장애가 발생했습니다. 잠시 후 다시 시도해 주시기 바랍니다.");
    }
  };

  const field: FieldSetInputProps[] = [
    {
      type: "text",
      name: "ad_alloc_date",
      placeholder: "예 ) 2023.05.19",
      className: "btn_bottomsheet",
      fieldLabelText: dateLabel,
      mandatory: true,
      readOnly: true,
      onClick: sheetState.show,
      value: sheetState.emitState?.alloc_date || "",
      valid: yup.mixed().test("ad_alloc_date is a required field", "required", (value) => {
        if (sheetState.emitState?.alloc_date) {
          return true;
        } else {
          return false;
        }
      }),
    },
    {
      type: "hidden",
      hiddenType: "time",
      hiddenFunction: sheetState.show,
      cascadeField: "ad_alloc_date",
      name: "ad_alloc_time",
      fieldLabelText: "광고 참여 시간대",
      mandatory: true,
      value: sheetState.emitState?.alloc_time || [],
      valid: yup.mixed().test("ad_alloc_time is a required field", "required", (value) => {
        if (sheetState.emitState?.alloc_time) {
          return true;
        } else {
          return false;
        }
      }),
    },
    {
      type: "text",
      name: "email",
      fieldLabelText: "회신받을 이메일",
      mandatory: true,
      placeholder: "회신 받을 이메일을 입력하세요.",
      noti: "잘못된 이메일 주소 입력 시, 문의 처리가 불가할 수 있습니다.",
      // valid: yup.string().email("이메일 형식이 맞지 않습니다.").required(),
      valid: yup
        .string()
        .required()
        .matches(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "이메일 형식이 맞지 않습니다."),
    },
    {
      type: "textarea",
      name: "cs_contents",
      fieldLabelText: "문의 내용",
      mandatory: true,
      placeholder: "문의할 내용을 입력하세요.",
      noti: "고객님의 소중한 개인정보는 입력하지 않도록 주의 부탁 드립니다.",
      valid: yup.string().required(),
    },
    {
      type: "file",
      name: "files",
      id: "files",
      fieldLabelText: "첨부파일",
      mandatory: false,
      noti: "이미지 파일만 가능 , 최대 용량 20MB",
      cascadeField: "hidden_field",
    },
    {
      fieldLabelText: "개인정보 수집 및 이용 동의",
      mandatory: true,
      name: "privacy",
      valid: yup.string().required("개인정보 수집 및 이용이 동의되지 않았습니다."),
      formGroup: {
        formType: "radio",
        form: [
          {
            type: "radio",
            labelText: "개인정보 수집 및 이용 동의",
            name: "privacy",
            id: "privacy",
            defaultChecked: false,
            value: "allow",
          },
        ],
      },
      children: <FieldDesc {...privacyDesc} />,
    },
  ];

  return (
    <>
      <InquiryFormWrapper>
        <div className="page_top_header">
          <h2 className="txt_title">Planet AD 광고 문의 페이지</h2>
        </div>
        <FieldSetHookForm legend="Planet AD 광고 문의 페이지" showSubmit={true} onSubmit={handleSubmit} inputs={field} />
        <DatePickerSheet show={sheetState.showState} onClose={sheetState.show} onEmit={sheetState.emit} />
      </InquiryFormWrapper>
      <toast.container />
    </>
  );
};

export default InquiryForm;
export type { InquiryFormProps };
